/**
 * The address checks that need no network: scheme, blocked names and private IP literals. Kept free of
 * imports so the edge function can run the same refusal as the Nitro route.
 */

const BLOCKED_HOSTNAMES = new Set([
  'localhost',
  '0.0.0.0',
  'metadata.google.internal',
  'metadata.google',
]);

const BLOCKED_SUFFIXES = ['.local', '.internal', '.localhost'];

export function isBlockedHostname(hostname: string): boolean {
  const lower = hostname.toLowerCase();
  if (BLOCKED_HOSTNAMES.has(lower)) return true;
  return BLOCKED_SUFFIXES.some((suffix) => lower.endsWith(suffix));
}

export function parseIpv4(ip: string): number[] | null {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  const nums = parts.map(Number);
  if (nums.some((n) => isNaN(n) || n < 0 || n > 255)) return null;
  return nums;
}

export function isPrivateIp(ip: string): boolean {
  if (ip === '::1') return true;
  if (ip.startsWith('fc') || ip.startsWith('fd')) return true;
  if (ip.startsWith('fe80')) return true;

  const cleanIp = ip.replace(/^\[|\]$/g, '');
  if (cleanIp === '::1') return true;
  if (cleanIp.startsWith('fc') || cleanIp.startsWith('fd')) return true;
  if (cleanIp.startsWith('fe80')) return true;

  const parts = parseIpv4(cleanIp);
  if (!parts) return false;

  const [a, b] = parts;

  if (a === 127) return true;
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 169 && b === 254) return true;
  if (a === 0) return true;

  return false;
}

export function validateResolvedIp(ip: string): { valid: true } | { valid: false; reason: string } {
  if (isPrivateIp(ip)) {
    return { valid: false, reason: `IP ${ip} is a private/reserved address` };
  }
  return { valid: true };
}

export function validateUrl(
  url: string,
): { valid: true; parsed: URL } | { valid: false; reason: string } {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { valid: false, reason: 'Invalid URL format' };
  }

  if (parsed.protocol !== 'https:') {
    return { valid: false, reason: `Only HTTPS URLs are allowed, got ${parsed.protocol}` };
  }

  if (isBlockedHostname(parsed.hostname)) {
    return { valid: false, reason: `Hostname ${parsed.hostname} is blocked` };
  }

  const ipCheck = parseIpv4(parsed.hostname);
  if (ipCheck && isPrivateIp(parsed.hostname)) {
    return { valid: false, reason: `IP address ${parsed.hostname} is a private/reserved address` };
  }

  const bracketless = parsed.hostname.replace(/^\[|\]$/g, '');
  if (
    bracketless === '::1' ||
    bracketless.startsWith('fc') ||
    bracketless.startsWith('fd') ||
    bracketless.startsWith('fe80')
  ) {
    return {
      valid: false,
      reason: `IPv6 address ${parsed.hostname} is a private/reserved address`,
    };
  }

  return { valid: true, parsed };
}
